import {
  BottomNavigation,
  Navigation,
  NavigationItem,
} from '@globalmed/atlas-ui';
import { Dispatch, SetStateAction } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from './NavPanel.module.css';
import { NavPanelHeader } from './components';
import { NavigationWithOrganizations } from '../NavigationWithOrganizations';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useVisibleOrganizations } from '@/hooks/useVisibleOrganizations';
import { INavigationItemPlus, ORGANIZATION_ROUTES } from '@/types';

/**
 * Atlas types the change handler with the base NavigationItem, while the items
 * we pass in are INavigationItemPlus and carry a route.
 * @param item - The navigation item reported by Atlas.
 * @returns True when the item carries a route.
 */
const hasRoute = (item: NavigationItem): item is INavigationItemPlus =>
  typeof (item as INavigationItemPlus).route === 'string';

type TProps = {
  translatedNavItems: INavigationItemPlus[];
  activeKey?: string;
  setActiveKey: Dispatch<SetStateAction<string | undefined>>;
};

/**
 * Main side/bottom navigation panel, switching between a bottom bar on small
 * screens, the organizations-aware navigation for users with organization
 * access, and the plain navigation otherwise.
 * @param props - Component props.
 * @param props.translatedNavItems - Navigation items with translated titles.
 * @param props.activeKey - Key of the currently active navigation item.
 * @param props.setActiveKey - Setter used when a navigation item is clicked.
 * @returns The navigation panel, or null while the active item is still
 * resolving.
 */
export const NavPanel = ({
  translatedNavItems,
  activeKey,
  setActiveKey,
}: TProps) => {
  const isSmallScreen = useMediaQuery('(max-width: 1280px)');
  const navigate = useNavigate();
  const location = useLocation();

  const { hasOrganizationsAccess } = useVisibleOrganizations();

  const handleChange = (item: NavigationItem) => {
    setActiveKey(item.key);
    if (hasRoute(item)) navigate(item.route);
  };

  const isOrganizationPage = ORGANIZATION_ROUTES.includes(location.pathname);

  if (
    !activeKey &&
    !location.pathname.match(/^\/encounter\/.+/) &&
    !isOrganizationPage
  ) {
    return null;
  }

  return (
    <>
      {isSmallScreen ? (
        <BottomNavigation
          items={translatedNavItems}
          onChange={handleChange}
          activeKey={activeKey}
        />
      ) : hasOrganizationsAccess ? (
        <NavigationWithOrganizations
          items={translatedNavItems}
          activeKey={activeKey}
          onChange={handleChange}
        />
      ) : (
        <Navigation
          className={styles.navPanel}
          items={translatedNavItems}
          onChange={handleChange}
          activeKey={activeKey}
          hideCollapse
        >
          <NavPanelHeader />
        </Navigation>
      )}
    </>
  );
};
